import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  doc,
  collection,
  getDocs,
  query,
  where,
  deleteDoc,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db } from "../firebase";
import "./UserProfile.css";

function UserProfile() {
  const [user, setUser] = useState(null);
  const [savedClasses, setSavedClasses] = useState([]);
  const [savedEvents, setSavedEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removingClass, setRemovingClass] = useState(null);
  const [removingEvent, setRemovingEvent] = useState(null);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        fetchSavedData(currentUser.uid);
      } else {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const fetchSavedData = async (userId) => {
    try {
      setLoading(true);

      // Fetch saved classes
      const savedClassesRef = collection(db, "userSavedClasses");
      const classQuery = query(savedClassesRef, where("userId", "==", userId));
      const classSnapshot = await getDocs(classQuery);

      const classes = [];
      classSnapshot.forEach((doc) => {
        classes.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      setSavedClasses(classes);

      // Fetch saved events
      const savedEventsRef = collection(db, "userSavedEvents");
      const eventQuery = query(savedEventsRef, where("userId", "==", userId));
      const eventSnapshot = await getDocs(eventQuery);

      const events = [];
      eventSnapshot.forEach((doc) => {
        events.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      setSavedEvents(events);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching saved data:", err);
      setLoading(false);
    }
  };

  const handleRemoveClass = async (savedClassId, classId) => {
    try {
      setRemovingClass(classId);

      // Remove from Firestore
      const savedClassRef = doc(db, "userSavedClasses", savedClassId);
      await deleteDoc(savedClassRef);

      // Update local state
      setSavedClasses(savedClasses.filter((c) => c.id !== savedClassId));
    } catch (err) {
      console.error("Error removing class:", err);
      alert("Error al eliminar la clase. Por favor, intenta de nuevo.");
    } finally {
      setRemovingClass(null);
    }
  };

  const handleRemoveEvent = async (savedEventId, eventId) => {
    try {
      setRemovingEvent(eventId);

      // Remove from Firestore
      const savedEventRef = doc(db, "userSavedEvents", savedEventId);
      await deleteDoc(savedEventRef);

      // Update local state
      setSavedEvents(savedEvents.filter((e) => e.id !== savedEventId));
    } catch (err) {
      console.error("Error removing event:", err);
      alert("Error al eliminar el evento. Por favor, intenta de nuevo.");
    } finally {
      setRemovingEvent(null);
    }
  };

  // Format date for event display
  const formatEventDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("es-AR", {
        weekday: "short",
        month: "short",
        day: "numeric",
      });
    } catch (err) {
      return dateString;
    }
  };

  if (!user) {
    return (
      <div className="profile-page">
        <div className="profile-container">
          <div className="login-prompt-section">
            <h1>Mi Perfil</h1>
            <p>Por favor, inicia sesión para ver tu perfil.</p>
            <Link to="/login" className="login-btn">
              Iniciar Sesión
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="profile-container">
        <div className="profile-header">
          <h1>Mi Perfil</h1>
          <p>Bienvenido a tu espacio personal</p>
        </div>

        <div className="profile-info">
          {/* <div className="profile-avatar">
            {user.photoURL ? (
              <img src={user.photoURL} alt={user.displayName} />
            ) : (
              <div className="avatar-placeholder">
                {user.displayName?.charAt(0) || user.email?.charAt(0)}
              </div>
            )}
          </div> */}

          <div className="profile-details">
            <h2>{user.displayName || "Usuario"}</h2>
            <p className="profile-email">{user.email}</p>

            <div className="profile-stats">
              <div className="stat-item">
                <span className="stat-number">{savedClasses.length}</span>
                <span className="stat-label">Clases guardadas</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{savedEvents.length}</span>
                <span className="stat-label">Eventos guardados</span>
              </div>
            </div>
          </div>
        </div>

        <div className="profile-sections">
          {/* Saved Classes Section */}
          <div className="profile-section">
            <div className="section-header">
              <h3>Mis Clases Guardadas</h3>
              <Link to="/classes" className="explore-link">
                Explorar más clases →
              </Link>
            </div>

            {loading ? (
              <div className="loading-state">
                <div className="loading-spinner-small"></div>
                <p>Cargando tus clases...</p>
              </div>
            ) : savedClasses.length === 0 ? (
              <div className="empty-state">
                <p>Aún no tienes clases guardadas</p>
                <Link to="/classes" className="explore-link">
                  Explorar clases →
                </Link>
              </div>
            ) : (
              <div className="saved-classes-grid">
                {savedClasses.map((savedClass) => (
                  <div key={savedClass.id} className="saved-class-card">
                    {/* <div className="class-image">
                      {savedClass.classImage ? (
                        <img
                          src={savedClass.classImage}
                          alt={savedClass.className}
                        />
                      ) : (
                        <div className="class-image-placeholder">
                          {savedClass.className?.charAt(0) || "C"}
                        </div>
                      )}
                    </div> */}
                    <div className="class-details">
                      <h4>{savedClass.className || "Clase sin nombre"}</h4>
                      <p className="class-schedule">
                        {savedClass.classSchedule || "Horario no especificado"}
                      </p>
                      <p className="class-price">
                        {savedClass.classPrice || "Consultar precio"}
                      </p>
                    </div>
                    <div className="class-actions">
                      <Link
                        to={`/classes/${savedClass.classId}`}
                        className="view-class-btn"
                      >
                        Ver detalles
                      </Link>
                      <button
                        onClick={() =>
                          handleRemoveClass(savedClass.id, savedClass.classId)
                        }
                        disabled={removingClass === savedClass.classId}
                        className="remove-class-btn"
                      >
                        {removingClass === savedClass.classId ? (
                          <span className="spinner-tiny"></span>
                        ) : (
                          "×"
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Saved Events Section */}
          <div className="profile-section">
            <div className="section-header">
              <h3>Mis Eventos Guardados</h3>
              <Link to="/agenda" className="explore-link">
                Ver agenda completa →
              </Link>
            </div>

            {loading ? (
              <div className="loading-state">
                <div className="loading-spinner-small"></div>
                <p>Cargando tus eventos...</p>
              </div>
            ) : savedEvents.length === 0 ? (
              <div className="empty-state">
                <p>Aún no tienes eventos guardados</p>
                <Link to="/agenda" className="explore-link">
                  Explorar eventos →
                </Link>
              </div>
            ) : (
              <div className="saved-events-grid">
                {savedEvents.map((savedEvent) => (
                  <div key={savedEvent.id} className="saved-event-card">
                    <div className="event-date-badge">
                      <span className="event-day">
                        {new Date(savedEvent.eventDate).getDate()}
                      </span>
                      <span className="event-month">
                        {new Date(savedEvent.eventDate).toLocaleDateString(
                          "es-AR",
                          {
                            month: "short",
                          },
                        )}
                      </span>
                    </div>
                    <div className="event-details">
                      <h4>{savedEvent.eventTitle || "Evento sin nombre"}</h4>
                      <p className="event-time">
                        {savedEvent.eventTime || "Horario no especificado"}
                      </p>
                      {savedEvent.eventDescription && (
                        <p className="event-desc-preview">
                          {savedEvent.eventDescription.substring(0, 60)}...
                        </p>
                      )}
                    </div>
                    <div className="event-actions">
                      {/* Note: You might want to create an EventDetail page or use a modal */}
                      <Link
                        to={`/agenda?event=${savedEvent.eventId}`}
                        className="view-event-btn"
                      >
                        Ver
                      </Link>
                      <button
                        onClick={() =>
                          handleRemoveEvent(savedEvent.id, savedEvent.eventId)
                        }
                        disabled={removingEvent === savedEvent.eventId}
                        className="remove-event-btn"
                      >
                        {removingEvent === savedEvent.eventId ? (
                          <span className="spinner-tiny"></span>
                        ) : (
                          "×"
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Upcoming Activities Section */}
          {/* <div className="profile-section">
            <h3>Próximas Actividades</h3>
            <div className="empty-state">
              <p>No tienes actividades programadas</p>
              <div className="explore-options">
                <Link to="/agenda" className="explore-link">
                  Ver eventos →
                </Link>
                <Link to="/classes" className="explore-link">
                  Ver clases →
                </Link>
              </div>
            </div>
          </div> */}
        </div>
      </div>
    </div>
  );
}

export default UserProfile;
